import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { ChangedSettingLog, SettingFormItem } from "./types"
import { ReadyDateConfiguration, ReadyDateOption, ReadyDateOptionUI, SettingFormItemValue } from "./types"
import { convertCentsToDollars } from "../../../utils/helpers"
import { cookieNames, deleteCookie, setCookie } from "../../../utils/cookies"
import { WrappedFormUtils } from "antd/lib/form/Form"
import { equals } from "ramda"

export const getArraySettingValue = (settings: SettingFormItem, key: string) =>
    settings[key].value ? settings[key].value.split(",") : []

export const getFormSettingValue = (settings: SettingFormItem, form: WrappedFormUtils, key: string) =>
    settings[key].value || form.getFieldValue(key)

export const fixNullStringValue = (settingValue: any) => (settingValue === "null" ? null : settingValue)

export const getCurrencyBasedSettingInDollars = (setting: SettingFormItem, key: string): SettingFormItem => {
    const val = setting[key].value
    const indeterminate = setting[key].indeterminate

    if (val != null) {
        return {
            [key]: {
                value: convertCentsToDollars(Number(val)),
                indeterminate: indeterminate,
            },
        }
    }

    return setting
}

export const getPercentageBasedSetting = (setting: SettingFormItem, key: string): SettingFormItem => {
    const val = setting[key].value
    const indeterminate = setting[key].indeterminate

    if (val != null) {
        return {
            [key]: {
                value: Number(val) / 1000,
                indeterminate: indeterminate,
            },
        }
    }

    return setting
}

export const getNewEnumSettingValueForPreviouslyBool = (
    settings: SettingFormItem,
    newSettingKey: string,
    oldSettingKey: string,
    oldSettingTrue: string,
    oldSettingFalse: string
): Maybe<string> => {
    if (settings[newSettingKey].value || settings[newSettingKey].value === undefined) {
        return settings[newSettingKey].value
    } else if (settings[oldSettingKey].value === undefined) {
        return undefined
    } else if (settings[oldSettingKey].value) {
        return oldSettingTrue
    } else {
        return oldSettingFalse
    }
}

export const resolveSelectedTerminalIdsCookies = (initialIds: string[], idsFromCookies: Maybe<string[]>): string[] => {
    if (!idsFromCookies || idsFromCookies.length === 0) {
        setCookie(cookieNames.terminalIds, JSON.stringify(initialIds))
        return initialIds
    }

    const invalidCookie = idsFromCookies.find(id => !initialIds.includes(id))

    if (invalidCookie) {
        deleteCookie(cookieNames.terminalIds)
        setCookie(cookieNames.terminalIds, JSON.stringify(initialIds))
        return initialIds
    }

    return idsFromCookies
}

export const updateReadyDateConfigurationByOption = (readyDate): ReadyDateConfiguration => {
    switch (readyDate.option) {
        case ReadyDateOptionUI.Off:
            return {
                ...readyDate,
                isEnabled: false,
                option: ReadyDateOption.Fixed,
            }
        case ReadyDateOptionUI.SameDay:
            return {
                ...readyDate,
                isEnabled: true,
                option: ReadyDateOption.Fixed,
                days: 0,
            }
        case ReadyDateOptionUI.NextDay:
            return {
                ...readyDate,
                isEnabled: true,
                option: ReadyDateOption.Fixed,
                days: 1,
            }
        case ReadyDateOptionUI.TwoPlusDays:
            return {
                ...readyDate,
                isEnabled: true,
                option: ReadyDateOption.Fixed,
                // for cases when reassigning from off back to 2 plus we don't want to set days to 2 if set before
                days: readyDate.days < 2 ? 2 : readyDate.days,
            }
        case ReadyDateOptionUI.Offset:
            return {
                ...readyDate,
                isEnabled: true,
                option: ReadyDateOption.Offset,
                days: 0,
            }
        default:
            return readyDate
    }
}

export const mapReadyDateSettingToUI = (
    readyDate: ReadyDateConfiguration,
    indeterminate: boolean
): ReadyDateOptionUI | undefined => {
    if (indeterminate) {
        return undefined
    }
    if (!readyDate.isEnabled) {
        return ReadyDateOptionUI.Off
    }
    if (readyDate.option === ReadyDateOption.Offset) {
        return ReadyDateOptionUI.Offset
    }
    switch (readyDate.days) {
        case 0:
            return ReadyDateOptionUI.SameDay
        case 1:
            return ReadyDateOptionUI.NextDay
        default:
            return ReadyDateOptionUI.TwoPlusDays
    }
}

export const getInitSettingValue = (setting: SettingFormItemValue, otherwise: any) => {
    return setting.value || setting.value === undefined ? setting.value : otherwise
}

export const getDaysOfWeek = (): string[] => [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
]

export const didSettingValueChange = (
    oldValues: Maybe<SettingFormItem>,
    newValues: Record<string, any>,
    key: string
): boolean => {
    if (!oldValues) {
        return true
    }

    const prevValueRaw = oldValues[key]?.value
    const nextValueRaw = newValues[key]

    if (nextValueRaw === undefined) {
        return false
    }

    if (prevValueRaw === "" && Array.isArray(nextValueRaw) && nextValueRaw.length === 0) {
        return false
    }

    if (typeof prevValueRaw === "string" && Array.isArray(nextValueRaw)) {
        return prevValueRaw !== nextValueRaw.join(",")
    }

    return !equals(prevValueRaw, nextValueRaw)
}

export const getLogChangedSetting = (
    oldValues: Maybe<SettingFormItem>,
    newValues: Record<string, any>,
    key: string
): ChangedSettingLog => {
    return {
        key: key,
        prev: oldValues?.[key]?.value ?? null,
        next: newValues[key] ?? null,
    }
}
